import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance } from 'vue';

// Components
import BackButton from '../back-button/back-button.vue';

// Store
import { useDiscountCodeStore } from '@white-label-store/discount-code';
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import { useUserInfoStore } from '@white-label-store/user-info';

// Helpers
import {
  goToApp,
  getDomainUrl,
  DOMAINS_NAMES,
  ECOMMERCE_URLS,
} from '@white-label-helper/switch-app';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { getAppVariable } from '@white-label-helper/get-app-variable';

// Types
import {
  APP_HERO_PRODUCTS,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';
import { CartItem } from '@white-label-types/parking-checkout';

interface Props {
  showExtrasPageButton?: boolean;
}

type BackDestination =
  | 'travel-extras'
  | 'partner-return-url'
  | 'product-extras'
  | 'quote'
  | 'search';


export default /*#__PURE__*/_defineComponent({
  __name: 'payment-page-navigation',
  props: {
    showExtrasPageButton: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { showExtrasPageButton: boolean };

const proxy = getCurrentInstance()?.proxy;



const heroProduct = computed(() =>
  getAppHeroProduct(APP_HERO_PRODUCTS.PARKING)
);

const isParking = computed(() =>
  heroProduct.value === PRODUCT_TYPES.PARKING
);

const isLounges = computed(() =>
  heroProduct.value === PRODUCT_TYPES.LOUNGES
);

const isFastTrack = computed(() =>
  heroProduct.value === PRODUCT_TYPES.FASTTRACK
);

const isTravelInsurance = computed(() =>
  heroProduct.value === PRODUCT_TYPES.TRAVEL_INSURANCE
);

const discountCode = computed(() => useDiscountCodeStore().discountCode);
const cartItems = computed(() => useCartCheckoutStore().readCartItems);
const outboundFlightId = computed(() => useUserInfoStore().outboundFlightLookupSelected?.id);

const displayFlightLookup = computed(() => {
  return getAppVariable('is_flight_lookup_enabled');
});

const cartHasExtrasProduct = computed(() => {
  return cartItems.value.some(
    (item: CartItem) => item.product_code === PRODUCT_TYPES.EXTRAS
  );
});

const backToSearchButtonText = computed(() => {
  if (isParking.value) return 'chooseSpace';
  if (isLounges.value) return 'chooseLounge';
  if (isFastTrack.value) return 'chooseFasttrack';
  return 'chooseSpace';
});

const hideChooseLoungeOnCheckout = computed(() => {
  return (
    props.showExtrasPageButton &&
    isLounges.value &&
    getFeatureFlag(FEATURE_FLAGS.EX_239_PURCHASE_SINGLE_EXTRAS) === true
  );
});

const showStep2Button = computed(() => !hideChooseLoungeOnCheckout.value);

const createNavigationQuery = (): Record<string, string> => {
  const query: Record<string, string> = {};

  if (discountCode.value) {
    query.discount = discountCode.value;
  }

  if (displayFlightLookup.value && outboundFlightId.value) {
    query.outboundFlight = outboundFlightId.value;
  }

  return query;
};

const navigateTo = (url: string, additionalParams: Record<string, string> = {}) => {
  const query = {
    ...createNavigationQuery(),
    ...additionalParams
  };

  goToApp(
    getDomainUrl(DOMAINS_NAMES.ECOMMERCE),
    url,
    query
  );
};

const backDestination = computed<BackDestination>(() => {
  if (isLounges.value && cartHasExtrasProduct.value) {
    return 'travel-extras';
  }
  if (isLounges.value && !cartHasExtrasProduct.value) {
    return 'partner-return-url';
  }
  if (cartHasExtrasProduct.value) {
    return 'product-extras';
  }
  if (isTravelInsurance.value) {
    return 'quote';
  }

  return 'search';
});

/**
 * Go back to the Search page
 */
const goBackToSearch = () => navigateTo(ECOMMERCE_URLS.SEARCH);

/**
 * Go back to the Product Extras page
 */
const goBackToProductExtras = () => navigateTo(ECOMMERCE_URLS.PRODUCT_EXTRAS);

/**
 * Go back to the Travel Extras page.
 */
const goBackToTravelExtras = () => {
  if (isLounges.value) {
    window.history.go(-1);
    return;
  }

  navigateTo(ECOMMERCE_URLS.TRAVEL_EXTRAS);
};

/**
 * Go back to the (Travel Insurance) Quote page
 */
const goBackToQuotePage = () => navigateTo(ECOMMERCE_URLS.QUOTE);

/**
 * Go back to the Partner Return URL if it exists, or to the Search page otherwise
 */
const goBackToPartnerReturnUrl = () => {
  const returnUrl = proxy?.$auth?.returnUrl;

  if (returnUrl) {
    window.location.assign(returnUrl);
    return;
  }

  goBackToSearch();
};

/**
 * Go back based on the current journey and cart contents.
 */
const goBack = () => {
  switch (backDestination.value) {
    case 'travel-extras':
      goBackToTravelExtras();
      break;
    case 'partner-return-url':
      goBackToPartnerReturnUrl();
      break;
    case 'product-extras':
      goBackToProductExtras();
      break;
    case 'quote':
      goBackToQuotePage();
      break;
    case 'search':
    default:
      goBackToSearch();
  }
};

return { __sfc: true,proxy, props, heroProduct, isParking, isLounges, isFastTrack, isTravelInsurance, discountCode, cartItems, outboundFlightId, displayFlightLookup, cartHasExtrasProduct, backToSearchButtonText, hideChooseLoungeOnCheckout, showStep2Button, createNavigationQuery, navigateTo, backDestination, goBackToSearch, goBackToProductExtras, goBackToTravelExtras, goBackToQuotePage, goBackToPartnerReturnUrl, goBack, BackButton }
}

})